import {
  DocumentTextIcon,
  MicrophoneIcon,
  PhotoIcon,
  TableCellsIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";
import { FileKind } from "../Menu/uploadFileConfig";
import { FileStatus, FilesItem } from "./api";

export const KindIcon: React.FC<{ kind: FileKind; className?: string }> = ({ kind, className }) => {
  // A caller-supplied text colour replaces the default grey (two text-* classes
  // on one element would fight over which wins).
  const cls = cn("size-5", !className?.includes("text-") && "text-base-content/60", className);
  switch (kind) {
    case "audio":
      return <MicrophoneIcon className={cls} />;
    case "video":
      return <VideoCameraIcon className={cls} />;
    case "image":
      return <PhotoIcon className={cls} />;
    case "spreadsheet":
      return <TableCellsIcon className={cls} />;
    default:
      return <DocumentTextIcon className={cls} />;
  }
};

const STATUS_CHIPS: Partial<Record<FileStatus, { label: string; cls: string }>> = {
  pending: { label: "Waiting", cls: "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" },
  uploading: { label: "Uploading", cls: "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" },
  processing: { label: "Processing", cls: "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" },
  failed: { label: "Failed", cls: "bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400" },
};

export const StatusChip: React.FC<{ status: FileStatus; error?: string }> = ({ status, error }) => {
  const chip = STATUS_CHIPS[status];
  if (!chip) return null;
  return (
    <span title={status === "failed" ? error : undefined} className={cn("px-2 py-0.5 rounded-full text-[11px] font-semibold", chip.cls)}>
      {chip.label}
    </span>
  );
};

export const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export const checkboxCls =
  "checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500";

// Whether a recording has a transcript yet. Non-audio files read as a dash:
// they are read for reports, not transcribed.
export const TranscriptChip: React.FC<{ file: FilesItem }> = ({ file }) => {
  if (file.kind !== "audio" && file.kind !== "video") return <span className="text-base-content/40">{"\u2014"}</span>;
  const inFlight = ["pending", "uploading", "processing"].includes(file.status);
  const [label, cls] = file.metadata.transcriptionUrl
    ? ["Transcribed", "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400"]
    : inFlight
      ? ["Transcribing", "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300"]
      : ["Not transcribed", "bg-base-200 text-base-content/60"];
  return <span className={cn("px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap", cls)}>{label}</span>;
};

// Small version of the Narrative / Interaction buttons in the upload modal.
const AUDIO_BADGES = {
  narrative: { label: "Narrative", cls: "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 border-blue-500" },
  interaction: { label: "Interaction", cls: "bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-400 border-purple-500" },
  other: { label: "Other", cls: "bg-base-300 text-gray-700 dark:text-base-content/80 border-gray-500 dark:border-base-content/40" },
} as const;

export const AudioTypeBadge: React.FC<{ type: "narrative" | "interaction" | "other" }> = ({ type }) => {
  const b = AUDIO_BADGES[type];
  return <span className={cn("inline-block border rounded-md px-1.5 py-px text-[10px] font-medium leading-4", b.cls)}>{b.label}</span>;
};
